import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms'
import { ChargingProcess } from 'src/app/shared/models/locations/charging-process.class'
import { userEv } from 'src/app/shared/models/users/user.class'

export function isAuthorizedAction (
  permissions: string[],
  action: string
): boolean {
  return permissions.includes(action)
}

export function formatDateToISO (date: string | null): string {
  if (!date) {
    return ''
  }

  //Ensure that the parts array has at least year, month, and day
  if (date.split('-').length < 3) {
    throw new Error('Invalid date format.')
  }

  const isoDateString = new Date(date).toISOString()

  return isoDateString
}

export function formatDateToServerTime (date: string | null): string {
  if (!date) {
    return ''
  }

  if (date.split('-').length < 3) {
    throw new Error('Invalid date format.')
  }

  // Already a full ISO/UTC string — pass through as-is
  if (date.includes('T') && date.endsWith('Z')) {
    return date
  }

  const d = new Date(date)
  const plusTwo = new Date(d.getTime() - 2 * 60 * 60 * 1000)
  return plusTwo.toISOString()
}

/**
 * Strips empty date fields from an API form payload.
 * Removes any key whose value is { start, end } where both are null/empty.
 */
export function cleanFormPayload (form: any): any {
  if (!form || typeof form !== 'object') {
    return form
  }

  const cleaned = { ...form }
  for (const key of Object.keys(cleaned)) {
    const val = cleaned[key]
    if (val && typeof val === 'object' && 'start' in val && 'end' in val) {
      if (!val.start && !val.end) {
        delete cleaned[key]
      }
    }
  }
  return cleaned
}

//confirmPassword is a UI-only "passwords match" field: the backend never uses
//it and the request logger doesn't mask it, so it must not leave the client.
export function stripConfirmPassword (form: any): any {
  if (!form || typeof form !== 'object') {
    return form
  }
  const cleaned = { ...form }
  delete cleaned.confirmPassword
  return cleaned
}

export const noWhitespacesOnly = (control: AbstractControl) =>
  `${control.value || ''}`.trim().length ? null : { required: true }

/**
 * Trims leading/trailing whitespace and collapses internal whitespace runs to
 * a single space for a free-text value. Returns the original value untouched
 * when it isn't a string; returns null when the value is whitespace-only so it
 * is treated as "no filter" rather than an empty-string query.
 */
export function sanitizeText (value: any): any {
  if (typeof value !== 'string') {
    return value
  }
  const cleaned = value.trim().replace(/\s+/g, ' ')
  return cleaned.length ? cleaned : null
}

/**
 * Sanitizes every free-text string control in a filter form in-place so the
 * cleaned value is sent to the API and reflected back in the input field.
 * Recurses one level into nested groups but skips date range groups
 * ({ start, end }), whose values are not user-typed free text.
 */
export function sanitizeFilterStrings (group: FormGroup): void {
  Object.keys(group.controls).forEach((key) => {
    const control = group.get(key)
    if (control instanceof FormGroup) {
      const value = control.value
      const isDateRange =
        value && typeof value === 'object' && 'start' in value && 'end' in value
      if (!isDateRange) {
        sanitizeFilterStrings(control)
      }
      return
    }
    if (typeof control.value === 'string') {
      control.setValue(sanitizeText(control.value), { emitEvent: false })
    }
  })
}

export const namePattern =
  /^[-'a-zA-Z0-9 \u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F]+$/

export const splitAndCleanValidator = (
  control: AbstractControl
): { [key: string]: any } | null => {
  if (typeof control.value === 'string') {
    const arrayOfStrings = control.value
      .split('\n')
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
    //Prevent infinite loop
    control.setValue(arrayOfStrings, { emitEvent: false })
  }
  //No validation errors
  return null
}

export enum UnitOfMeasure {
  W = 'W',
  KW = 'kW',
  WH = 'Wh',
  KWH = 'kWh'
}

export const normalizeUnits = (value, unit) => {
  switch (unit) {
    case UnitOfMeasure.WH:
      //Wh -> kWh
      //Used Accuracy: One decimal points
      //simplification of Math.round((value / 1000) * 10) / 10
      return Math.round(value / 100) / 10
    case UnitOfMeasure.W:
      //W -> kW
      return Math.round(value / 100) / 10
    default:
      return value
  }
}

//helper enum for charger details
export enum CommandEloaded {
  GUN_STAT = 'GUN_STAT',
  CHARGE_INFO = 'CHARGE_INFO',
  CHARGE_START = 'CHARGE_START',
  CHARGE_STOP = 'CHARGE_STOP',
  CHARGE_ALLOW = 'CHARGE_ALLOW',
  CHARGE_DENY = 'CHARGE_DENY'
}

export enum CommandOCPP {
  REMOTE_START_TRANSACTION = 'RemoteStartTransaction',
  REMOTE_STOP_TRANSACTION = 'RemoteStopTransaction',
  RESET = 'Reset',
  TRIGGER_MESSAGE = 'TriggerMessage',
  UNLOCK_CONNECTOR = 'UnlockConnector'
}

export enum MessageTriggerOCPP {
  BOOT_NOTIFICATION = 'BootNotification',
  HEARTBEAT = 'Heartbeat',
  STATUS_NOTIFICATION = 'StatusNotification',
  METER_VALUES = 'MeterValues'
}

export const isEmptyObject = (obj): boolean => {
  for (const key in obj) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key]?.length !== 0) {
      return false
    }
  }

  return true
}

export const getAveragePower = (charge: ChargingProcess): number => {
  const { energy, startDate, stopDate } = charge
  if (!startDate || energy === undefined) {
    return
  }
  //Calculate the time charging
  const start = new Date(startDate)
  const stop = stopDate ? new Date(stopDate) : new Date()

  const timeChargingInMs = stop.getTime() - start.getTime()
  const timeChargingInH = timeChargingInMs / 1000 / 60 / 60
  //Calculate average power
  return Math.round((energy / timeChargingInH) * 10) / 10
}

export const calculateKW = (current: number, voltage: number): number =>
  Math.round((current * voltage) / 100) / 10

export const openingTimesValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const { twentyfourseven, regularHours } = control.value
  if (twentyfourseven) {
    return null
  }
  //todo: double check the validation here
  if (regularHours.filter(({ weekday }) => weekday !== undefined).length) {
    return null
  }
  return { regularHours: true }
}

export const timeOrderValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const periodBegin = control.get('periodBegin')?.value
  const periodEnd = control.get('periodEnd')?.value

  if (periodBegin && periodEnd && periodBegin >= periodEnd) {
    return { timeOrderInvalid: true } //Period end is earlier than period begin
  }

  return null //No error
}

//Custom validator for time zone
export const timeZoneValidator =
  (timeZones: string[]): ValidatorFn =>
    (control: AbstractControl): { [key: string]: any } | null =>
      timeZones.includes(control.value)
        ? null
        : { invalidTimeZone: { value: control.value } }

export const isIFrame = (
  input: HTMLElement | null
): input is HTMLIFrameElement => input !== null && input.tagName === 'IFRAME'

//Custom Validator for energySources FormArray
export const percentageSumValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  if (control instanceof FormArray) {
    //Only apply the validation if the array is not empty
    if (control.controls.length === 0) {
      return null //Allow empty array
    }

    const sum = control.controls.reduce((total, group) => {
      const percentageControl = group.get('percentage')
      return total + (percentageControl ? percentageControl.value : 0)
    }, 0)

    return sum === 100 ? null : { sumNotEqualTo100: true }
  }
  return null
}

//Custom validator for plugs FormArray
export const atLeastOneInstalledPlug = (
  control: AbstractControl
): ValidationErrors | null => {
  const plugs = control.value as { number: number }[]
  const hasInstalledPlug = plugs.some((plug) => plug.number === 1)

  return hasInstalledPlug ? null : { noInstalledPlug: true }
}

export const validEvccidFormat = (
  control: AbstractControl
): ValidationErrors | null => {
  const evccidPattern = /^[A-Za-z0-9_-]{5,20}$/
  return control.value && !evccidPattern.test(control.value)
    ? { invalidEvccidFormat: true }
    : null
}

export const evccidNotExistedValidator =
  (existingEVs: userEv[]): ValidatorFn =>
    (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
      //No validation needed for empty input
        return null
      }
      const isExisted = existingEVs.some(
      // eslint-disable-next-line max-len
        (ev) => ev.evccid.evccid === control.value || ev.evccid === control.value
      )
      return isExisted ? { evccidExists: true } : null
    }

export const uniqueInstanceInArrayVal: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  if (!(control instanceof FormArray)) {
    return null
  }

  const formArray = control as FormArray
  const seen = new Map<string, number[]>()

  formArray.controls.forEach((group, index) => {
    const category = (group as FormGroup).get('category')?.value
    if (category) {
      if (!seen.has(category)) {
        seen.set(category, [])
      }
      seen.get(category)!.push(index)
    }
  })

  //Clear old errors first
  formArray.controls.forEach((group) => {
    const categoryCtrl = (group as FormGroup).get('category')
    if (categoryCtrl?.hasError('duplicateCategory')) {
      const newErrors = { ...categoryCtrl.errors }
      delete newErrors.duplicateCategory
      categoryCtrl.setErrors(Object.keys(newErrors).length ? newErrors : null)
    }
  })

  //Apply errors to duplicates
  let hasDuplicate = false
  seen.forEach((indexes) => {
    if (indexes.length > 1) {
      hasDuplicate = true
      indexes.forEach((i) => {
        const categoryCtrl = (formArray.at(i) as FormGroup).get('category')
        if (categoryCtrl) {
          categoryCtrl.setErrors({
            ...(categoryCtrl.errors || {}),
            duplicateCategory: true
          })
        }
      })
    }
  })

  return hasDuplicate ? { duplicateCategory: true } : null
}

export const formatDuration = (minutes: number): string => {
  if (!minutes && minutes !== 0) {
    return ''
  }
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}
